/**
 * Complete a partial address from call context, before it reaches the geocoder.
 *
 * Callers say "Dundas Street West", not "5230 Dundas Street West, Toronto, ON,
 * Canada". A bare street name is ambiguous on every continent, which is how pins
 * ended up thousands of km away. Handing the geocoder the city/province/country
 * we can already infer is a better fix than biasing or filtering its output,
 * because it removes the ambiguity instead of cleaning up after it.
 *
 * ## Why this is deterministic and not an LLM call
 *
 * The two signals asked for — the caller's phone area code and the other location
 * entities on the same call — are both exact lookups. They need no inference, and
 * they resolve in microseconds with no network.
 *
 * That matters because this runs inline before every geocode. api-ai-engine
 * already has an LLM jurisdiction enricher (`_enrich_jurisdiction_with_ai`,
 * `POST /tools/geocode/enrich`) and the AI geocode fallback beside it is measured
 * at **8,008 ms p50 / 37,772 ms p95** (see
 * `api-ai-engine/tests/test_geocode_model_pin.py`). Putting that on this path
 * would be the exact "backs up and causes trouble on its own" failure. So:
 *
 *   - This module is the DEFAULT: synchronous, zero-network, always safe to call.
 *   - The LLM enricher stays available for deeper gaps, but must be called
 *     out-of-band (fire-and-forget, after a pin already exists) — never awaited
 *     here.
 *
 * ## Precedence — most trustworthy first, and never overwrite
 *
 *   1. Fields already on the entity's own `jurisdiction` — untouched, always.
 *   2. Text already present in the address string. If the caller named a city we
 *      do not append a different one.
 *   3. Sibling location entities on the same call with confirmed jurisdiction.
 *   4. The caller's phone area code.
 *
 * Nothing here overwrites a known value; it only fills blanks. If a signal is
 * ambiguous it contributes nothing rather than guessing — an area code shared by
 * two provinces yields the country only.
 */

/** A jurisdiction as carried on a Location entity (subset we can infer). */
export interface InferredJurisdiction {
  city?: string | null;
  state_province?: string | null;
  country?: string | null;
}

type JurisdictionField = keyof InferredJurisdiction;

/** Stored inside provider jurisdiction so query-derived results cannot seed later queries. */
export const QUERY_CONTEXT_PROVENANCE_FIELD = '__neutralaiz_query_context';
export const QUERY_CONTEXT_PROVENANCE_INFERRED = 'inferred';

export function jurisdictionWasInferredFromQuery(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  return (value as Record<string, unknown>)[QUERY_CONTEXT_PROVENANCE_FIELD]
    === QUERY_CONTEXT_PROVENANCE_INFERRED;
}

export interface AddressContextInput {
  /** The location's own address text, if any. */
  address?: string | null;
  /** Fallback descriptors used when there is no address. */
  name?: string | null;
  shortDescription?: string | null;
  /** Anything already known for THIS location. Never overwritten. */
  jurisdiction?: InferredJurisdiction | null;
  /** Caller's phone number in any format; only the area code is read. */
  phone?: string | null;
  /** Other locations on the same call, ideally ones already geocoded. */
  siblings?: Array<{
    jurisdiction?: InferredJurisdiction | null;
    geocodingStatus?: string | null;
    queryContextDerived?: boolean;
  }> | null;
  /**
   * Hard cap on the returned query length, if the caller has one.
   *
   * Completion must respect it ITSELF rather than leaving the caller to slice the
   * result: slicing after appending cuts a field mid-word ("…, Ontari"), which
   * invents a place name and is worse than never appending at all. With a cap set,
   * fields are appended only if they fit WHOLE, and a base already over the cap is
   * truncated with nothing appended.
   */
  maxLength?: number | null;
}

export interface AddressContextResult {
  /** The query to geocode: original text plus any inferred fields appended. */
  query: string;
  /** Only the fields this module ADDED. Empty when nothing was missing. */
  inferred: InferredJurisdiction;
  /** Every field appended to the query, including already-known entity fields. */
  appended: InferredJurisdiction;
  /** Which signal supplied each appended field — for logging and provenance. */
  sources: Partial<Record<JurisdictionField, 'entity' | 'sibling' | 'area_code'>>;
  /** True when the query was left exactly as it came in. */
  unchanged: boolean;
}

// ---------------------------------------------------------------------------
// NANP area codes
// ---------------------------------------------------------------------------
// Canada is enumerated in full — it is the deployment's home country, so the
// province is worth having. An unknown NANP code yields nothing: NANP includes
// the Caribbean and several other countries, so "not Canadian" does NOT imply
// "United States".
const CANADIAN_AREA_CODES: Record<string, string | null> = {
  // Alberta
  '403': 'Alberta', '587': 'Alberta', '780': 'Alberta', '825': 'Alberta', '368': 'Alberta',
  // British Columbia
  '236': 'British Columbia', '250': 'British Columbia', '257': 'British Columbia', '604': 'British Columbia',
  '672': 'British Columbia', '778': 'British Columbia',
  // Manitoba
  '204': 'Manitoba', '431': 'Manitoba', '584': 'Manitoba',
  // New Brunswick
  '506': 'New Brunswick', '428': 'New Brunswick',
  // Newfoundland and Labrador
  '709': 'Newfoundland and Labrador', '879': 'Newfoundland and Labrador',
  // Nova Scotia / Prince Edward Island share 902 and 782 — province is ambiguous,
  // so it is deliberately null: country only.
  '902': null, '782': null,
  // Northwest Territories / Nunavut / Yukon all share 867 — ambiguous.
  '867': null,
  // Ontario
  '226': 'Ontario', '249': 'Ontario', '289': 'Ontario', '343': 'Ontario', '365': 'Ontario',
  '382': 'Ontario', '416': 'Ontario', '437': 'Ontario', '519': 'Ontario', '548': 'Ontario',
  '613': 'Ontario', '647': 'Ontario', '683': 'Ontario', '705': 'Ontario', '742': 'Ontario',
  '753': 'Ontario', '807': 'Ontario', '905': 'Ontario', '942': 'Ontario',
  // Quebec
  '263': 'Quebec', '354': 'Quebec', '367': 'Quebec', '418': 'Quebec', '438': 'Quebec',
  '450': 'Quebec', '468': 'Quebec', '514': 'Quebec', '579': 'Quebec', '581': 'Quebec',
  '819': 'Quebec', '873': 'Quebec',
  // Saskatchewan
  '306': 'Saskatchewan', '474': 'Saskatchewan', '639': 'Saskatchewan',
};

// Deliberately not pre-seeded: 273 (Eastern Quebec) is scheduled for 2027-02-27
// and 851 (NS/PE) for 2028-05-27. Until service begins, treating them as caller
// evidence creates jurisdiction from numbers that cannot yet be subscribers.

/**
 * Extract the NANP area code from a phone number in any common format.
 *
 * Handles `+1 (416) 555-0117`, `416-555-0117`, `4165550117`, `1-416-555-0117`.
 * Returns null unless the digits form a valid NANP number, because a partial or
 * international number would otherwise contribute a confidently wrong region.
 */
export function areaCodeFromPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  // Strip the country code only when doing so leaves a full 10-digit number.
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  const area = digits.slice(0, 3);
  // NANP NPA format is NXX. N11 values are service codes, not geographic NPAs.
  if (!/^[2-9]\d{2}$/.test(area) || area.slice(1) === '11') return null;
  return area;
}

/** Region implied by a phone number. `{}` when nothing can be concluded. */
export function regionFromPhone(phone: string | null | undefined): InferredJurisdiction {
  const area = areaCodeFromPhone(phone);
  if (!area) return {};
  if (Object.prototype.hasOwnProperty.call(CANADIAN_AREA_CODES, area)) {
    const province = CANADIAN_AREA_CODES[area];
    return province ? { state_province: province, country: 'Canada' } : { country: 'Canada' };
  }
  return {};
}

const norm = (v: unknown): string =>
  typeof v === 'string' ? v.trim().toLowerCase() : '';

const normPhrase = (v: unknown): string =>
  typeof v === 'string'
    ? v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ')
    : '';

/** Does the address text already name this value? Prevents contradictory appends. */
function textMentions(text: string, value: string | null | undefined): boolean {
  const needle = normPhrase(value);
  if (!needle) return false;
  // Two-letter jurisdiction codes are ordinary words surprisingly often
  // (notably Ontario's `ON`). Only treat a short code as jurisdiction text when
  // it is an explicit comma-delimited address component, never when it merely
  // appears inside the free-form street/business description.
  if (/^[a-z]{1,2}$/.test(needle)) {
    return text.split(',').slice(1).some((part) => normPhrase(part) === needle);
  }
  const haystack = normPhrase(text);
  return ` ${haystack} `.includes(` ${needle} `);
}

const CANADIAN_PROVINCE_ALIASES = new Map<string, string>([
  ['ab', 'Alberta'], ['alberta', 'Alberta'],
  ['bc', 'British Columbia'], ['british columbia', 'British Columbia'],
  ['mb', 'Manitoba'], ['manitoba', 'Manitoba'],
  ['nb', 'New Brunswick'], ['new brunswick', 'New Brunswick'],
  ['nl', 'Newfoundland and Labrador'], ['newfoundland and labrador', 'Newfoundland and Labrador'],
  ['ns', 'Nova Scotia'], ['nova scotia', 'Nova Scotia'],
  ['nt', 'Northwest Territories'], ['northwest territories', 'Northwest Territories'],
  ['nu', 'Nunavut'], ['nunavut', 'Nunavut'],
  ['on', 'Ontario'], ['ontario', 'Ontario'],
  ['pe', 'Prince Edward Island'], ['pei', 'Prince Edward Island'], ['prince edward island', 'Prince Edward Island'],
  ['qc', 'Quebec'], ['quebec', 'Quebec'], ['québec', 'Quebec'],
  ['sk', 'Saskatchewan'], ['saskatchewan', 'Saskatchewan'],
  ['yt', 'Yukon'], ['yukon', 'Yukon'],
]);

const COUNTRY_ALIASES = new Map<string, string>([
  ['ca', 'Canada'], ['can', 'Canada'], ['canada', 'Canada'],
  ['us', 'United States'], ['usa', 'United States'], ['united states', 'United States'],
  ['united states of america', 'United States'],
  ['australia', 'Australia'], ['united kingdom', 'United Kingdom'], ['uk', 'United Kingdom'],
]);

function textJurisdiction(text: string): InferredJurisdiction {
  const parts = text.split(',').map((part) => part.trim()).filter(Boolean);
  const detected: InferredJurisdiction = {};

  for (const part of parts.slice(1)) {
    const normalized = normPhrase(part);
    const country = COUNTRY_ALIASES.get(normalized);
    if (country) {
      detected.country = country;
      continue;
    }
    const province = CANADIAN_PROVINCE_ALIASES.get(normalized);
    if (province) {
      detected.state_province = province;
      continue;
    }
    if (/^[a-z]\d[a-z]\s*\d[a-z]\d$/i.test(part) || /^\d{5}(?:-\d{4})?$/.test(part)) {
      continue;
    }
    if (!detected.city) detected.city = part;
  }
  return detected;
}

function hasJurisdiction(jurisdiction: InferredJurisdiction | null | undefined): boolean {
  return !!jurisdiction && (['city', 'state_province', 'country'] as JurisdictionField[])
    .some((field) => typeof jurisdiction[field] === 'string' && !!jurisdiction[field]?.trim());
}

/** Select one coherent jurisdiction; never vote each field independently. */
function coherentSiblingJurisdiction(
  siblings: AddressContextInput['siblings'],
): InferredJurisdiction | null {
  if (!siblings || siblings.length === 0) return null;
  const safe = siblings.filter((s) =>
    s && !s.queryContextDerived && !jurisdictionWasInferredFromQuery(s.jurisdiction)
      && hasJurisdiction(s.jurisdiction),
  );
  const confirmed = safe.filter((s) => s.geocodingStatus === 'success');
  const pool = confirmed.length > 0 ? confirmed : safe;

  const groups = new Map<string, { jurisdiction: InferredJurisdiction; count: number; completeness: number }>();
  for (const sibling of pool) {
    const jurisdiction = sibling.jurisdiction || {};
    const key = (['city', 'state_province', 'country'] as JurisdictionField[])
      .map((field) => norm(jurisdiction[field])).join('|');
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    groups.set(key, {
      jurisdiction,
      count: 1,
      completeness: (['city', 'state_province', 'country'] as JurisdictionField[])
        .filter((field) => !!norm(jurisdiction[field])).length,
    });
  }
  if (groups.size === 0) return null;

  let best: { jurisdiction: InferredJurisdiction; count: number; completeness: number } | null = null;
  for (const entry of groups.values()) {
    if (!best || entry.count > best.count
      || (entry.count === best.count && entry.completeness > best.completeness)) {
      best = entry;
    }
  }
  return best?.jurisdiction || null;
}

/**
 * Fill missing city / province / country from call context and return the query
 * to geocode.
 *
 * A no-op — `unchanged: true`, query byte-identical — whenever the address already
 * carries the fields, or no signal supplies anything. Cheap enough to call
 * unconditionally: a few string ops and a map lookup, no network, no allocation
 * beyond the result.
 */
export function completeAddressFromContext(
  input: AddressContextInput,
): AddressContextResult {
  const base = String(input.address || input.name || input.shortDescription || '').trim();
  const known = jurisdictionWasInferredFromQuery(input.jurisdiction)
    ? {}
    : (input.jurisdiction || {});
  const inferred: InferredJurisdiction = {};
  const appended: InferredJurisdiction = {};
  const sources: AddressContextResult['sources'] = {};

  if (!base) {
    return { query: base, inferred, appended, sources, unchanged: true };
  }

  const fromPhone = regionFromPhone(input.phone);
  const inText = textJurisdiction(base);
  let fromSibling = coherentSiblingJurisdiction(input.siblings);

  // Any explicit jurisdiction in the address outranks sibling context. Discard
  // the whole sibling tuple on a conflict so its remaining fields cannot be
  // spliced onto a different place (city, province, or country).
  const jurisdictionFields: JurisdictionField[] = ['city', 'state_province', 'country'];
  const siblingConflictsWithText = fromSibling && jurisdictionFields.some((field) =>
    !!fromSibling?.[field] && !!inText[field]
      && !textMentions(String(inText[field]), fromSibling[field]),
  );
  if (siblingConflictsWithText) {
    fromSibling = null;
  }

  const fields = jurisdictionFields;
  for (const field of fields) {
    // 1. Already known for this location: include it in the outbound query, but
    // never mutate or label it as inferred.
    const existing = known[field];
    let value = typeof existing === 'string' && existing.trim() ? existing.trim() : null;
    let source: 'entity' | 'sibling' | 'area_code' | null = value ? 'entity' : null;

    // 2. One coherent sibling jurisdiction, then 3. the phone. City is never
    // inferred from a phone: an area code covers too many municipalities.
    if (!value) {
      const siblingValue = fromSibling?.[field];
      if (typeof siblingValue === 'string' && siblingValue.trim()) {
        value = siblingValue.trim();
        source = 'sibling';
      }
    }
    if (!value && field !== 'city') {
      const phoneValue = fromPhone[field];
      if (typeof phoneValue === 'string' && phoneValue.trim()) {
        value = phoneValue;
        source = 'area_code';
      }
    }
    if (!value) continue;

    // 4. Text is already in the query. A conflicting explicit value blocks the
    // append; merely checking for the proposed value would create Ottawa,Toronto.
    if (textMentions(base, value)) continue;
    const explicit = inText[field];
    if (explicit && !textMentions(explicit, value)) continue;

    appended[field] = value;
    if (source !== 'entity') inferred[field] = value;
    if (source) sources[field] = source;
  }

  const limit =
    typeof input.maxLength === 'number' && input.maxLength > 0 ? input.maxLength : null;

  // A base already at or over the cap leaves no room; truncate and append nothing
  // rather than emitting a fragment.
  if (limit !== null && base.length >= limit) {
    return { query: base.slice(0, limit), inferred: {}, appended: {}, sources: {}, unchanged: true };
  }

  // Append greedily, and only what fits WHOLE. `inferred`/`sources` are then
  // narrowed to what actually landed in the query, so the caller's log cannot
  // claim a field that was dropped.
  let query = base;
  const applied: InferredJurisdiction = {};
  const appliedAppended: InferredJurisdiction = {};
  const appliedSources: AddressContextResult['sources'] = {};
  for (const field of fields) {
    const value = appended[field];
    if (typeof value !== 'string' || !value) continue;
    const candidate = `${query}, ${value}`;
    if (limit !== null && candidate.length > limit) continue;
    query = candidate;
    const source = sources[field];
    appliedAppended[field] = value;
    if (source !== 'entity') applied[field] = value;
    if (source) appliedSources[field] = source;
  }

  if (query === base) {
    return { query: base, inferred: {}, appended: {}, sources: {}, unchanged: true };
  }

  return {
    query,
    inferred: applied,
    appended: appliedAppended,
    sources: appliedSources,
    unchanged: false,
  };
}
